// tools/durability-lab/js/app.js
const els = {
  file: document.getElementById("fitFile"),
  sport: document.getElementById("sport"),
  env: document.getElementById("environment"),
  ftp: document.getElementById("ftp"),
  hrMax: document.getElementById("hrMax"),
  tempC: document.getElementById("tempC"),
  fueling: document.getElementById("fueling"),
  runLite: document.getElementById("runLite"),
  analyzeBtn: document.getElementById("analyzeBtn"),
  copyBtn: document.getElementById("copyBtn"),
  downloadBtn: document.getElementById("downloadBtn"),
  status: document.getElementById("status"),
  results: document.getElementById("results"),
  chartContainer: document.getElementById("chartContainer"),
  driftChart: document.getElementById("driftChart"),
};

let lastReportJson = null;

// Worker module (ESM)
const worker = new Worker(new URL("../worker/durabilityWorker.js", import.meta.url), { type: "module" });

worker.addEventListener("message", (ev) => {
  const msg = ev.data;

  if (msg.type === "PROGRESS") {
    setStatus(msg.message);
    return;
  }

  if (msg.type === "RESULT") {
    renderResult(msg.payload);
    return;
  }

  if (msg.type === "ERROR") {
    setStatus(`Erreur: ${msg.error}`);
    els.analyzeBtn.disabled = false;
    return;
  }
});

els.file.addEventListener("change", () => {
  els.analyzeBtn.disabled = !els.file.files?.length;
  els.copyBtn.disabled = true;
  els.downloadBtn.disabled = true;
  lastReportJson = null;
  setStatus(els.file.files?.length ? "Fichier prêt. Clique sur Analyser." : "Sélectionne un fichier FIT.");
  els.results.textContent = "Aucun résultat.";
});

els.analyzeBtn.addEventListener("click", async () => {
  const f = els.file.files?.[0];
  if (!f) return;

  els.analyzeBtn.disabled = true;
  els.copyBtn.disabled = true;
  els.downloadBtn.disabled = true;
  lastReportJson = null;

  setStatus("Lecture du fichier…");

  try {
    const buf = await f.arrayBuffer();

    const userInputs = {
      ftp: toNumOrNull(els.ftp.value),
      cs: null,
      hrMax: toNumOrNull(els.hrMax.value),
      tempC: toNumOrNull(els.tempC.value),
      fueling_gph: toNumOrNull(els.fueling.value),
      runLiteAllowed: !!els.runLite.checked,
    };

    worker.postMessage({
      type: "ANALYZE",
      payload: {
        sport: els.sport.value,
        environment: els.env.value,
        fileName: f.name,
        fitArrayBuffer: buf,
        userInputs,
        configOverrides: {},
      }
    }, [buf]); // transfer ownership => no copy
  } catch (e) {
    setStatus(`Erreur lecture fichier: ${e?.message || String(e)}`);
    els.analyzeBtn.disabled = false;
  }
});

els.copyBtn.addEventListener("click", async () => {
  if (!lastReportJson) return;
  try {
    await navigator.clipboard.writeText(JSON.stringify(lastReportJson, null, 2));
    setStatus("Rapport JSON copié dans le presse-papier.");
  } catch {
    setStatus("Impossible de copier (permissions navigateur).");
  }
});

els.downloadBtn.addEventListener("click", () => {
  if (!lastReportJson) return;

  // Générer le nom du fichier
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
  const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
  const sport = lastReportJson.sport || 'unknown';
  const fileName = `durability-${sport}-${dateStr}-${timeStr}.json`;

  // Créer le blob et télécharger
  const json = JSON.stringify(lastReportJson, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  setStatus(`Fichier téléchargé: ${fileName}`);
});

function setStatus(s) {
  els.status.textContent = s;
}

function renderResult(result) {
  els.analyzeBtn.disabled = false;

  // Reset classes
  els.results.className = "results";

  if (result.status === "NO_SCORE") {
    els.results.classList.add("no-score");
    els.results.textContent = formatNoScore(result);
    els.chartContainer.style.display = "none";
    setStatus("Analyse terminée — NO SCORE.");
    els.copyBtn.disabled = false;
    els.downloadBtn.disabled = false;
    lastReportJson = result.report ?? result;
    return;
  }

  if (result.status === "OK") {
    els.results.classList.add("success");
    els.results.textContent = formatOk(result);

    // Afficher le graphique
    renderDriftChart(result);
    els.chartContainer.style.display = "block";

    setStatus("Analyse terminée — Score calculé.");
    els.copyBtn.disabled = false;
    els.downloadBtn.disabled = false;
    lastReportJson = result.report ?? result;
    return;
  }

  els.results.textContent = `Statut inattendu: ${result.status}`;
  els.chartContainer.style.display = "none";
  setStatus("Analyse terminée (statut inattendu).");
}

function formatNoScore(r) {
  const flags = r.flags || [];
  const sport = r.sport || "bike";

  // Identifier la raison principale
  let mainReason = "Aucun bloc stable détecté";
  let diagnosis = "";
  let protocol = "";

  // Analyser les flags pour comprendre pourquoi
  const hasNoSteady = flags.some(f => f.code === "NO_STEADY_BLOCK_FOUND");
  const hasTooShort = flags.some(f => f.code === "BLOCK_TOO_SHORT");
  const hasStops = flags.some(f => f.code?.includes("STOP"));
  const hasRamp = flags.some(f => f.code === "RAMP_DETECTED");
  const hasMissingData = flags.some(f => f.code?.includes("MISSING"));

  if (hasNoSteady) {
    mainReason = "Aucun bloc stable détecté";
    diagnosis = "Pacing trop variable, relances, bosses, vent, ou séance groupe.";
  } else if (hasTooShort) {
    mainReason = "Bloc trop court";
    diagnosis = "Durée insuffisante pour mesurer la dérive (besoin de 90'+ en général, ou 75'+ en course).";
  } else if (hasStops) {
    mainReason = "Trop d'arrêts";
    diagnosis = "Feux, pauses, ravitos debout. Le test nécessite un effort continu.";
  } else if (hasMissingData) {
    mainReason = "Données manquantes ou incomplètes";
    diagnosis = "Capteur HR instable, trous de puissance/vitesse, ou fichier corrompu.";
  } else if (hasRamp) {
    mainReason = "Progression détectée";
    diagnosis = "L'intensité augmente trop pendant l'effort (rampe). On veut mesurer le drift à intensité stable.";
  }

  // Protocole adapté au sport
  if (sport === "bike") {
    protocol = [
      "📋 PROTOCOLE VÉLO (recommandé)",
      "",
      "Option #1 : Home-trainer (optimal)",
      "• 15' échauffement progressif",
      "• 90' steady à 70-78% FTP (allure IM)",
      "• Puissance + cadence stables",
      "• Fueling habituel (60-90 g/h)",
      "",
      "Option #2 : Outdoor (si HT impossible)",
      "• Parcours plat, même boucle à chaque test",
      "• ZÉRO stop (pas de feux)",
      "• Intensité verrouillée"
    ].join("\n");
  } else {
    protocol = [
      "📋 PROTOCOLE COURSE (recommandé)",
      "",
      "Option #1 : Tapis (optimal)",
      "• 10-15' échauffement",
      "• 75-90' steady en haut de Z2 (RPE 4-5/10)",
      "• Vitesse stable",
      "• Fueling si >75' (30-60 g/h)",
      "",
      "Option #2 : Outdoor (si tapis impossible)",
      "• Piste ou boucle plate",
      "• ZÉRO stop",
      "• Allure stable au cardio"
    ].join("\n");
  }

  const flagsList = flags.map(f => `  • [${f.severity}] ${f.code}: ${f.message}`).join("\n");

  return [
    "═══════════════════════════════════════════════",
    "❌ TEST NON EXPLOITABLE (NO SCORE)",
    "═══════════════════════════════════════════════",
    "",
    `🔍 RAISON PRINCIPALE : ${mainReason}`,
    diagnosis,
    "",
    "📊 Détails techniques :",
    flagsList || "  • (non spécifié)",
    "",
    "═══════════════════════════════════════════════",
    "💡 C'EST NORMAL !",
    "═══════════════════════════════════════════════",
    "",
    "Ce n'est pas un bug. L'outil cherche un bloc STABLE pour",
    "mesurer la dérive. Sortie libre, sortie groupe, parcours",
    "vallonné = incompatibles.",
    "",
    "Pour obtenir un score, fais un TEST type :",
    "",
    protocol,
    "",
    "═══════════════════════════════════════════════",
    "📖 Consulte le MODE D'EMPLOI ci-dessus pour plus",
    "   de détails sur les protocoles de test.",
    "═══════════════════════════════════════════════"
  ].join("\n");
}

function formatOk(r) {
  const m = r.metrics || {};
  const f = (r.flags || []).map(x => `- [${x.severity}] ${x.code}: ${x.message}`).join("\n");
  const wbStart = r.workBlock?.start != null ? secToHMS(r.workBlock.start) : "?";
  const wbEnd = r.workBlock?.end != null ? secToHMS(r.workBlock.end) : "?";
  return [
    `STATUT: OK`,
    `Sport: ${r.sport} | Env: ${r.environment}`,
    "",
    `Work block: ${secToHMS(r.workBlock?.duration_s)} (${wbStart} → ${wbEnd})`,
    `Segments: early ${secToHMS(r.segments?.early?.end - r.segments?.early?.start)} | late ${secToHMS(r.segments?.late?.end - r.segments?.late?.start)}`,
    "",
    `HR drift: ${fmt(m.hr_drift_pct)}%`,
    `EF drop: ${fmt(m.ef_drop_pct)}%`,
    `Output change: ${fmt(m.output_change_pct)}%`,
    `CV early/late: ${fmt(m.cv_early * 100)}% / ${fmt(m.cv_late * 100)}%`,
    "",
    `Confidence: ${r.confidence?.score ?? "?"} (${r.confidence?.level ?? "?"})`,
    "",
    f ? `Flags:\n${f}` : "Flags: none"
  ].join("\n");
}

/* ==================== VISUALISATION GRAPHIQUE ==================== */

function renderDriftChart(result) {
  const canvas = els.driftChart;
  const ctx = canvas.getContext('2d');
  const metrics = result.metrics || {};
  const sport = result.sport || "bike";

  // Configuration responsive
  const containerWidth = els.chartContainer.offsetWidth;
  const dpr = window.devicePixelRatio || 1;
  const width = Math.min(containerWidth - 32, 800); // Max 800px
  const height = 400;

  // Configurer le canvas pour la haute résolution
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  ctx.scale(dpr, dpr);

  // Données
  const hrEarly = metrics.hr_early || 0;
  const hrLate = metrics.hr_late || 0;
  const outEarly = metrics.out_early || 0;
  const outLate = metrics.out_late || 0;
  const hrDrift = metrics.hr_drift_pct || 0;
  const outputChange = metrics.output_change_pct || 0;

  // Labels
  const outputLabel = sport === "bike" ? "Puissance (W)" : "Vitesse (m/s)";
  const outputUnit = sport === "bike" ? "W" : "m/s";

  // Marges et dimensions
  const margin = { top: 40, right: 20, bottom: 60, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Couleurs BPC
  const colorHR = '#DC2626'; // BPC Primary
  const colorOutput = '#0ea5e9'; // Sky blue
  const colorEarly = '#f8fafc'; // Light gray
  const colorLate = '#e2e8f0'; // Gray

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // Titre
  ctx.fillStyle = '#1e293b';
  ctx.font = 'bold 18px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Comparaison Early vs Late', width / 2, 25);

  // Zone de dessin
  const x0 = margin.left;
  const y0 = margin.top;

  // Échelles
  const maxHR = Math.max(hrEarly, hrLate) * 1.1;
  const maxOutput = Math.max(outEarly, outLate) * 1.1;

  const scaleHR = (val) => y0 + chartHeight - (val / maxHR) * chartHeight;
  const scaleOutput = (val) => y0 + chartHeight - (val / maxOutput) * chartHeight;

  // Groupes de barres
  const groupWidth = chartWidth / 2 - 40;
  const barWidth = groupWidth / 2 - 10;
  const earlyX = x0 + 20;
  const lateX = x0 + chartWidth / 2 + 20;

  // Fonction pour dessiner une barre
  const drawBar = (x, value, scale, color, label, unit) => {
    const barHeight = chartHeight - (scale(value) - y0);
    const barY = scale(value);

    // Barre
    ctx.fillStyle = color;
    ctx.fillRect(x, barY, barWidth, barHeight);

    // Bordure
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, barY, barWidth, barHeight);

    // Valeur au-dessus
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 14px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${Math.round(value)} ${unit}`, x + barWidth / 2, barY - 8);
  };

  // Background zones Early/Late
  ctx.fillStyle = colorEarly;
  ctx.fillRect(x0, y0, chartWidth / 2, chartHeight);
  ctx.fillStyle = colorLate;
  ctx.fillRect(x0 + chartWidth / 2, y0, chartWidth / 2, chartHeight);

  // Ligne de base
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + chartHeight);
  ctx.lineTo(x0 + chartWidth, y0 + chartHeight);
  ctx.stroke();

  // Barres Early
  drawBar(earlyX, hrEarly, scaleHR, colorHR, 'HR Early', 'bpm');
  drawBar(earlyX + barWidth + 10, outEarly, scaleOutput, colorOutput, outputLabel, outputUnit);

  // Barres Late
  drawBar(lateX, hrLate, scaleHR, colorHR, 'HR Late', 'bpm');
  drawBar(lateX + barWidth + 10, outLate, scaleOutput, colorOutput, outputLabel, outputUnit);

  // Labels groupes
  ctx.fillStyle = '#475569';
  ctx.font = 'bold 16px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('EARLY', x0 + chartWidth / 4, y0 + chartHeight + 30);
  ctx.fillText('LATE', x0 + 3 * chartWidth / 4, y0 + chartHeight + 30);

  // Légende
  const legendY = y0 + chartHeight + 50;
  const legendX = x0;

  ctx.fillStyle = colorHR;
  ctx.fillRect(legendX, legendY, 20, 12);
  ctx.fillStyle = '#1e293b';
  ctx.font = '13px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Fréquence cardiaque (bpm)', legendX + 28, legendY + 10);

  ctx.fillStyle = colorOutput;
  ctx.fillRect(legendX + 220, legendY, 20, 12);
  ctx.fillStyle = '#1e293b';
  ctx.fillText(outputLabel, legendX + 248, legendY + 10);

  // Indicateurs de drift
  const driftX = width - 180;
  const driftY = y0 + 10;

  ctx.fillStyle = '#fffbeb';
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 2;
  ctx.fillRect(driftX, driftY, 160, 80);
  ctx.strokeRect(driftX, driftY, 160, 80);

  ctx.fillStyle = '#92400e';
  ctx.font = 'bold 13px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Drift cardiaque:', driftX + 10, driftY + 20);
  ctx.fillStyle = hrDrift > 0 ? '#dc2626' : '#10b981';
  ctx.font = 'bold 20px Inter, sans-serif';
  ctx.fillText(`${hrDrift > 0 ? '+' : ''}${hrDrift.toFixed(1)}%`, driftX + 10, driftY + 45);

  ctx.fillStyle = '#92400e';
  ctx.font = 'bold 13px Inter, sans-serif';
  ctx.fillText('Output change:', driftX + 10, driftY + 60);
  ctx.fillStyle = '#475569';
  ctx.font = '14px Inter, sans-serif';
  ctx.fillText(`${outputChange > 0 ? '+' : ''}${outputChange.toFixed(1)}%`, driftX + 10, driftY + 75);
}

/* ==================== FORMATTERS ==================== */

function fmt(x) {
  if (x == null || Number.isNaN(x)) return "—";
  return (Math.round(x * 10) / 10).toString();
}

function toNumOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function secToHMS(sec) {
  if (!Number.isFinite(sec)) return "—";
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}
